import React, { createContext, useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import { getUniversalNav } from '../../queries';

export const HeaderContext = createContext();

/**
 * Adds a unique ID to each menu item in the Mega Menu.  This ID can be used as the key property
 * when using react to iterate through the nav items.
 * @param {Object} data JSON containing menu navigation configuration
 * @returns {Object} The JSON augmented with a unique ID for each menu node
 */
export function addUniqueIdToEachMenuItem(data) {
  const dataWithId = { ...data };
  let id = 0;
  const level1 = dataWithId.menuNavigation;
  if (level1) {
    level1.forEach((value, lvl1Index) => {
      level1[lvl1Index].id = id;
      id += 1;
      const level2 = dataWithId.menuNavigation[lvl1Index].children;
      /* istanbul ignore else */
      if (level2) {
        level2.forEach((child, lvl2Index) => {
          level2[lvl2Index].id = id;
          id += 1;
          const level3 = dataWithId.menuNavigation[lvl1Index].children[lvl2Index].links;
          /* istanbul ignore else */
          if (level3) {
            level3.forEach((link, lvl3Index) => {
              level3[lvl3Index].id = id;
              id += 1;
            });
          }
        });
      }
    });
  }

  return dataWithId;
}

function HeaderData({ children }) {
  const { loading, error, data: navData } = useQuery(getUniversalNav);
  const processedData = navData?.getUniversalNav
    ? addUniqueIdToEachMenuItem(navData?.getUniversalNav)
    : null;

    const contextValue = useMemo(() => ({
      isLoading: loading,
      isError: !!error,
      isSuccess: !loading && !error && !!processedData,
      data: processedData,
    }), [loading, error, processedData]);
    
  return (
    <HeaderContext.Provider value={contextValue}>
      {children}
    </HeaderContext.Provider>
  );
}

HeaderData.meta = {
  VERSION: '1.0.0',
};

HeaderData.propTypes = {
  children: PropTypes.node.isRequired,
};

export default HeaderData;
